/**
 * Pure state transitions for remote pack subscriptions (Phase 4.3). The store
 * orchestrates fetches and re-merges; the decision logic lives here so it can be
 * unit-tested without a store, network, or localStorage.
 */
import type { RawContentPack } from '../types/content'
import type { PackPointer, RemotePackSubscription, SubscriptionScope } from '../types/subscription'
import type { FetchPackResult } from './syncPack'

const isParty = (s: RemotePackSubscription): boolean => s.scope?.kind === 'party'

const nowIso = () => new Date().toISOString()

/** A fresh subscription, before its first sync. Auto-updating by default. */
export function newSubscription(pointer: PackPointer): RemotePackSubscription {
  return {
    subscriptionId: crypto.randomUUID(),
    pointer,
    status: { state: 'never-synced' },
    options: { autoUpdate: true },
    subscribedAt: nowIso(),
  }
}

/**
 * The cached content of subscriptions, in order, that should feed the resolver.
 * Paused subscriptions still serve their cache — pausing stops *syncing*, not
 * serving — so the only filter is "has it ever synced". Ordered party-before-
 * personal so that `mergeContent`'s later-wins gives **personal precedence** on
 * colliding entity ids.
 */
export function subscriptionPacks(subscriptions: RemotePackSubscription[]): RawContentPack[] {
  const ordered = [...subscriptions.filter(isParty), ...subscriptions.filter((s) => !isParty(s))]
  return ordered.flatMap((s) => (s.cached ? [s.cached.pack] : []))
}

/**
 * Reconcile the party-scoped subscriptions inside `subscriptions` against the
 * `desired` set of party pointers (from the party store), leaving personal
 * subscriptions untouched. Pure: the store applies `next` and syncs `added`.
 *
 * - dedup by URL; a URL already covered by a personal subscription is skipped
 *   (personal wins, and we fetch each URL once);
 * - an unchanged party URL keeps its existing cache (just refreshes the scope);
 * - a party URL no longer desired is dropped → its cache is evicted (leave/kick).
 */
export function reconcilePartySubscriptions(
  subscriptions: RemotePackSubscription[],
  desired: { pointer: PackPointer; scope: SubscriptionScope }[],
): { next: RemotePackSubscription[]; added: string[] } {
  const personal = subscriptions.filter((s) => !isParty(s))
  const personalUrls = new Set(personal.map((s) => s.pointer.url))
  const existingParty = new Map(subscriptions.filter(isParty).map((s) => [s.pointer.url, s]))

  const seen = new Set<string>()
  const party: RemotePackSubscription[] = []
  const added: string[] = []
  for (const d of desired) {
    const url = d.pointer.url
    if (personalUrls.has(url) || seen.has(url)) continue
    seen.add(url)
    const existing = existingParty.get(url)
    if (existing) {
      party.push({ ...existing, scope: d.scope, pointer: { ...existing.pointer, ...d.pointer } })
    } else {
      const sub: RemotePackSubscription = { ...newSubscription(d.pointer), scope: d.scope }
      party.push(sub)
      added.push(sub.subscriptionId)
    }
  }
  return { next: [...personal, ...party], added }
}

/**
 * Whether an on-load auto-sync should re-fetch this subscription now: not paused,
 * and last checked longer ago than the cooldown (never-checked always qualifies).
 */
export function isSyncDue(sub: RemotePackSubscription, now: number, cooldownMs: number): boolean {
  if (sub.options.paused) return false
  const last = sub.status.lastCheckedAt ? Date.parse(sub.status.lastCheckedAt) : 0
  return now - last >= cooldownMs
}

/**
 * Fold a fetch result into a subscription. On success it either applies the new
 * content (first sync, auto-update, or a forced manual apply), flags an available
 * update (manual mode with an existing cache), or marks it current (unchanged). On
 * failure it records the error but keeps the last-known-good cache intact, so a
 * dead link or offline player never loses content. Pure: the caller re-merges.
 */
export function applyFetch(
  sub: RemotePackSubscription,
  result: FetchPackResult,
  force = false,
): RemotePackSubscription {
  const at = nowIso()

  if (!result.ok) {
    const state = result.error.kind === 'unreachable' ? 'offline' : 'error'
    return { ...sub, status: { ...sub.status, state, lastError: result.error } }
  }

  const { pack, revision } = result
  // First successful fetch resolves the pointer's advisory identity from the pack.
  const pointer: PackPointer = {
    ...sub.pointer,
    packId: sub.pointer.packId || pack.id,
    name: sub.pointer.name ?? pack.name,
  }

  if (sub.cached?.revision.contentHash === revision.contentHash) {
    return { ...sub, pointer, status: { ...sub.status, state: 'current', lastCheckedAt: at, lastError: undefined } }
  }

  if (!sub.cached || sub.options.autoUpdate || force) {
    return {
      ...sub,
      pointer,
      cached: { pack, revision },
      status: { state: 'current', lastCheckedAt: at, appliedRevision: revision, lastError: undefined },
    }
  }

  // Manual mode with an existing cache: flag the update, keep serving the old copy.
  return {
    ...sub,
    pointer,
    status: { ...sub.status, state: 'update-available', lastCheckedAt: at, availableRevision: revision, lastError: undefined },
  }
}
